import { resolveMainComputationSnapshot } from "../../../MainTaxComputation/utils/resolveMainComputationSnapshot";
import {
  computationPeriod,
  dateOnly,
  matchingComputations,
  validateDraft,
  validateSetup,
} from "./preparation";

export const yesNoLabel = (value) =>
  value === true ? "Yes" : value === false ? "No" : "Not available";

export function startupEligibilityLabel(
  value,
  taxPeriodType,
  yearOfAssessment
) {
  if (value === null || value === undefined) return "Not available";
  const isConfig = value && typeof value === "object" && !Array.isArray(value);
  const read = (period) => {
    if (!isConfig) return Boolean(value);
    const periodValue =
      value[period] ?? value.current ?? value.preceding ?? null;
    return periodValue === null ? null : Boolean(periodValue);
  };
  if (taxPeriodType === "cross_ya") {
    const year = Number(yearOfAssessment);
    return `YA ${year - 1}: ${yesNoLabel(read("preceding"))} · YA ${year}: ${yesNoLabel(read("current"))}`;
  }
  return yesNoLabel(read("current"));
}

// A review is an input snapshot, never an optimisation result. Result fields
// (transfers, adjustments, reconciliation) are only added by the allocation run.
export function buildReview(selected, settings) {
  const issues = Object.entries(validateSetup(settings)).map(
    ([field, message]) => ({ field, message, step: 0 })
  );
  if (selected.length < 2)
    issues.push({ message: "Select at least two companies.", step: 1 });
  const companies = selected.map((draft, stableOrder) => {
    Object.entries(validateDraft(draft)).forEach(([field, message]) =>
      issues.push({ companyId: draft.id, field, message, step: 2 })
    );
    if (!draft.confirmed)
      issues.push({
        companyId: draft.id,
        message: "Check this company's figures and shareholding details.",
        step: 2,
      });
    if (!matchingComputations([draft.computation], settings).length)
      issues.push({
        companyId: draft.id,
        message: "Choose a computation matching the case YA and FYE.",
        step: 1,
      });
    let resolution = null;
    if (draft.sourceStatus === "loaded" && draft.sourceDoc) {
      try {
        resolution = resolveMainComputationSnapshot({
          computation: draft.computation,
          doc: draft.sourceDoc,
          settings,
        });
      } catch (error) {
        issues.push({ companyId: draft.id, message: error.message, step: 2 });
      }
    }
    const period = computationPeriod(draft.computation);
    return {
      id: draft.id,
      name: draft.name,
      uen: draft.uen,
      isExternal: Boolean(draft.isExternal),
      stableOrder,
      confirmed: draft.confirmed,
      computationId: draft.computation.tc_id,
      financialYearStart: period?.start,
      financialYearEnd: period?.end,
      taxPeriodType: draft.computation.taxPeriodType,
      isStartup:
        draft.sourceDoc &&
        Object.prototype.hasOwnProperty.call(draft.sourceDoc, "isStartup")
          ? draft.sourceDoc.isStartup
          : null,
      shareholdingMaintainedThroughout: draft.shareholdingMaintainedThroughout,
      conditionMetOnFinalDay:
        draft.shareholdingMaintainedThroughout === true ||
        draft.conditionMetOnFinalDay === true,
      continuousQualifyingDays:
        draft.shareholdingMaintainedThroughout === true
          ? period?.days
          : Number(draft.continuousQualifyingDays),
      sourceSavedAt: draft.sourceSavedAt,
      resolution,
    };
  });
  return {
    schemaVersion: 1,
    settings: {
      ...settings,
      commonFinancialYearEnd: dateOnly(settings.commonFinancialYearEnd),
    },
    companies,
    issues,
  };
}

// Columns remain separate for each YA. Original-currency inputs must never be
// added to SGD tax results or to another company's different source currency.
// Group Relief loss balances come from the shared SGD resolver output so that
// brought-forward losses and current-year losses remain separate.
const RESULT_LABEL_BY_SOURCE_ID = {
  adjusted_profit_before_capital_allowances: "Adjusted Profit",
  unutilised_ca_bf: "Prior-Year Unutilised CA",
  current_year_ca: "Current-Year CA",
  add_other_sourced_income: "Other-Sourced Income",
  total_investment_income: "Other-Sourced Income",
  unutilised_losses_bf: "Prior-Year Unutilised Losses",
  current_year_losses: "Current-Year Losses",
  unutilised_donations_bf: "Prior-Year Unutilised Donations",
  current_year_donations: "Current-Year Donations",
};

const RESULT_LABEL_BY_PERIOD_STEP = {
  "Net Investment Income": "Adjusted Profit",
  "Adjusted Profit Before Capital Allowances": "Adjusted Profit",
  "Adjusted Profit After Capital Allowances": "Adjusted Profit after CA",
  "Adjusted Profit After Unutilised Losses": "Statutory income less losses",
  "Adjusted Profit After Unutilised Donations": "Assessable / Chargeable income",
  "Unutilised Capital Allowances c/f": "Adjusted Profit after CA",
  "Unutilised Losses c/f": "Statutory income less losses",
  "Unutilised Donations c/f": "Assessable / Chargeable income",
  "Current Year Losses": "Current-Year Losses",
};

const resultLabelForSource = (id, fallback) =>
  RESULT_LABEL_BY_SOURCE_ID[id] || fallback;

const resultLabelForPeriodStep = (label) =>
  RESULT_LABEL_BY_PERIOD_STEP[label] || label;

export function detailedReviewRows(companies) {
  const rows = new Map();
  const add = (key, label, companyId, value, currency) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue === 0) return;
    if (!rows.has(key)) rows.set(key, { key, label, cells: {} });
    rows.get(key).cells[companyId] = { value: numericValue, currency };
  };
  companies.forEach(({ id, resolution }) => {
    const detailed = resolution?.groupRelief?.detailedOutput || {};
    const investmentCompany = resolution?.companyType === "investment";
    const addLossRows = () => {
      add(
        "source:unutilised_losses_bf",
        RESULT_LABEL_BY_SOURCE_ID.unutilised_losses_bf,
        id,
        Number(detailed.priorYearLosses || 0),
        "SGD"
      );
      add(
        "source:current_year_losses",
        RESULT_LABEL_BY_SOURCE_ID.current_year_losses,
        id,
        Number(detailed.currentYearLosses || 0),
        "SGD"
      );
    };
    let lossesAdded = false;
    (resolution?.sourceLines || []).forEach((line) => {
      if (line.id === "unutilised_losses_bf") {
        addLossRows();
        lossesAdded = true;
        return;
      }
      const adjustedProfit =
        line.id === "adjusted_profit_before_capital_allowances";
      add(
        `source:${line.id}`,
        resultLabelForSource(line.id, line.label),
        id,
        adjustedProfit ? Number(detailed.adjustedProfit || 0) : line.value,
        adjustedProfit ? "SGD" : line.currency || resolution.currency
      );
    });
    if (!lossesAdded) addLossRows();
    resolution?.periods.forEach((period) => {
      period.steps
        ?.filter(
          (step) =>
            step.label !== "Chargeable Income (Before Exempt)" &&
            !(investmentCompany && step.label === "Net Investment Income") &&
            (period.key !== "current" || step.label !== "Current Year Losses")
        )
        .forEach((step) =>
          add(
            `${period.label}:${step.label}`,
            `${period.label} · ${resultLabelForPeriodStep(step.label)}`,
            id,
            Number(step.value),
            step.currency || resolution.currency
          )
        );
      add(
        `${period.label}:ci`,
        `${period.label} · Assessable / Chargeable income before exemption`,
        id,
        period.chargeableIncome,
        "SGD"
      );
      add(
        `${period.label}:tax`,
        `${period.label} · Tax payable before ECI`,
        id,
        period.taxPayableBeforeEci,
        "SGD"
      );
    });
  });
  const sourceOrder = new Map([
    ["source:adjusted_profit_before_capital_allowances", 0],
    ["source:unutilised_ca_bf", 1],
    ["source:current_year_ca", 2],
    ["source:add_other_sourced_income", 3],
    ["source:total_investment_income", 3],
    ["source:unutilised_losses_bf", 4],
    ["source:current_year_losses", 5],
    ["source:unutilised_donations_bf", 6],
    ["source:current_year_donations", 7],
  ]);
  return [...rows.values()].sort(
    (left, right) =>
      (sourceOrder.get(left.key) ?? 1000) -
      (sourceOrder.get(right.key) ?? 1000),
  );
}
